import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import Toast from './Toast';
import './SavedPrompts.css';
import './HelpModal.css';

// Edit Modal Component
const EditPromptModal = ({ prompt, isOpen, onClose, onSave }) => {
  const [editedPrompt, setEditedPrompt] = useState(prompt || {});
  const [isPublic, setIsPublic] = useState(prompt?.isPublic || false);

  useEffect(() => {
    if (prompt) {
      setEditedPrompt(prompt);
      setIsPublic(prompt.isPublic);
    }
  }, [prompt]);

  // Handle body scroll when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleSave = () => {
    onSave({ ...editedPrompt, isPublic });
    onClose();
  };

  if (!isOpen || !prompt) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="help-modal-overlay" onClick={handleOverlayClick}>
      <div className="help-modal" style={{ maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div className="help-modal-header">
          <h2>Edit Your Prompt</h2>
          <button onClick={onClose} className="help-modal-close" title="Close">&times;</button>
        </div>
        
        <div className="help-modal-content" style={{ flex: 1, overflow: 'auto', padding: '2rem' }}>
          <div style={{ marginBottom:'1rem' }}>
            <label style={{ display:'block', marginBottom:6, fontWeight:600, color:'#2c3e50' }}>Display Name</label>
            <input
              type="text"
              value={editedPrompt?.displayName || ''}
              onChange={(e) => setEditedPrompt({ ...editedPrompt, displayName: e.target.value })}
              placeholder="Topic – Subtopic"
              style={{ width:'100%', padding:'10px 12px', border:'2px solid #ced4da', borderRadius:8, fontSize:'1rem' }}
            />
          </div>

          <textarea
            value={editedPrompt?.content || ''}
            onChange={(e) => setEditedPrompt({...editedPrompt, content: e.target.value})}
            style={{
              width: '100%',
              height: '300px',
              padding: '15px',
              border: '2px solid #ced4da',
              borderRadius: '8px',
              fontSize: '1rem',
              fontFamily: 'Consolas, Monaco, Courier New, monospace',
              lineHeight: '1.5',
              background: 'white',
              color: '#495057',
              boxSizing: 'border-box',
              resize: 'vertical',
              marginBottom: '1rem'
            }}
            placeholder="Edit your prompt content here..."
          />
          
          <textarea
            value={editedPrompt.description || ''}
            onChange={(e) => setEditedPrompt({...editedPrompt, description: e.target.value})}
            style={{
              width: '100%',
              height: '80px',
              padding: '12px',
              border: '2px solid #ced4da',
              borderRadius: '8px',
              fontSize: '0.95rem',
              fontFamily: 'inherit',
              background: 'white',
              color: '#495057',
              boxSizing: 'border-box',
              resize: 'vertical',
              marginBottom: '1rem'
            }}
            placeholder="Description (optional)"
          />
          
          <label style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            cursor: 'pointer',
            fontSize: '1rem',
            color: '#495057'
          }}>
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              style={{
                width: '18px',
                height: '18px',
                cursor: 'pointer'
              }}
            />
            <span>Make this prompt public</span>
          </label>
        </div>
        
        <div className="help-modal-footer" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', padding: '1.5rem 2rem', backgroundColor: '#f8f9fa', borderTop: '1px solid #e9ecef', flexShrink: 0 }}>
          <button 
            onClick={onClose} 
            className="help-modal-btn help-modal-btn-cancel"
            style={{ padding: '0.75rem 1.5rem', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '1rem', minWidth: '100px', backgroundColor: '#6c757d', color: 'white' }}
          >
            Cancel
          </button>
          <button 
            onClick={handleSave} 
            className="help-modal-btn help-modal-btn-primary"
            style={{ padding: '0.75rem 1.5rem', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '1rem', minWidth: '100px', backgroundColor: '#007bff', color: 'white' }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

const SavedPrompts = () => {
  const [activeTab, setActiveTab] = useState('my-prompts');
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('recent');
  const [mySortBy, setMySortBy] = useState('recent'); // recent | oldest | displayName | topic
  const [mySortDir, setMySortDir] = useState('asc');   // asc | desc
  const [searchBy, setSearchBy] = useState('both');    // displayName | topic | both
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [publicConfirmOpen, setPublicConfirmOpen] = useState(false);
  const [promptToMakePublic, setPromptToMakePublic] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [likedPrompts, setLikedPrompts] = useState(new Set());
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTargetId, setShareTargetId] = useState(null);
  const [sharePrincipalType, setSharePrincipalType] = useState('team');
  const [sharePrincipalId, setSharePrincipalId] = useState('');
  const [shareRole, setShareRole] = useState('viewer');
  const [shareExpiresAt, setShareExpiresAt] = useState('');
  const [recipientGroups, setRecipientGroups] = useState([]);
  const [userTeams, setUserTeams] = useState([]);
  const [userOrgs, setUserOrgs] = useState([]);
  const [isTeamOrOrgUser, setIsTeamOrOrgUser] = useState(false);

  useEffect(() => {
    fetchPrompts();
  }, [activeTab, currentPage, searchTerm, sortBy]);
  // Prefill 30-day expiry and fetch share recipients on modal open
  useEffect(() => {
    const computeLocalDateTime = (d) => {
      // Format yyyy-MM-ddTHH:mm in local time
      const pad = (n) => String(n).padStart(2, '0');
      const yyyy = d.getFullYear();
      const MM = pad(d.getMonth() + 1);
      const dd = pad(d.getDate());
      const hh = pad(d.getHours());
      const mm = pad(d.getMinutes());
      return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
    };
    if (shareOpen) {
      // Default to now + 30 days
      const dt = new Date();
      dt.setDate(dt.getDate() + 30);
      setShareExpiresAt(computeLocalDateTime(dt));
      
      // Check if user is Team/Org user and fetch teams/orgs
      (async () => {
        try {
          const token = authService.getToken();
          const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
          
          // Check user roles
          const user = await authService.getUserInfo();
          const roles = user?.roles || [];
          const isTeamOrOrg = roles.some(r => 
            ['teamuser', 'teamadmin', 'enterpriseuser', 'enterpriseadmin'].includes(String(r).toLowerCase())
          );
          setIsTeamOrOrgUser(isTeamOrOrg);
          
          if (isTeamOrOrg) {
            // Fetch user's teams and orgs
            const [teamsRes, orgsRes] = await Promise.all([
              fetch('/api/teams/mine', { headers, credentials: 'include' }),
              fetch('/api/orgs/mine', { headers, credentials: 'include' })
            ]);
            
            let teams = [];
            let orgs = [];
            
            if (teamsRes.ok) {
              const teamsData = await teamsRes.json();
              teams = Array.isArray(teamsData) ? teamsData : [];
              setUserTeams(teams);
            }
            if (orgsRes.ok) {
              const orgsData = await orgsRes.json();
              orgs = Array.isArray(orgsData) ? orgsData : [];
              setUserOrgs(orgs);
            }
            
            // Default to team if available, otherwise org
            if (teams.length > 0) {
              setSharePrincipalType('team');
            } else if (orgs.length > 0) {
              setSharePrincipalType('org');
            }
          } else {
            // Fetch recipients for dropdown (Free/Pro users)
            const resp = await fetch('/api/shares/recipients', {
              headers,
              credentials: 'include'
            });
            if (!resp.ok) { setRecipientGroups([]); return; }
            const data = await resp.json();
            // Support either { groups:[...] } or direct array
            const groups = Array.isArray(data?.groups) ? data.groups : (Array.isArray(data) ? data : []);
            setRecipientGroups(groups || []);
            // Prefer 'user' selection path when members available
            if ((groups || []).some(g => Array.isArray(g.members) && g.members.length > 0)) {
              setSharePrincipalType('user');
            }
          }
        } catch {
          setRecipientGroups([]);
        }
      })();
    } else {
      // reset transient member choice when closing
      setRecipientGroups([]);
      setSharePrincipalId('');
    }
  }, [shareOpen]);

  useEffect(() => {
    const user = authService.getCurrentUser();
    setCurrentUser(user);
  }, []);

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      const token = authService.getToken();
      let endpoint = '/api/savedprompts/my-prompts';
      if (activeTab === 'community') endpoint = '/api/savedprompts/community';
      if (activeTab === 'team') endpoint = '/api/savedprompts/team';
      if (activeTab === 'org') endpoint = '/api/savedprompts/org';
      
      const params = new URLSearchParams({
        page: currentPage,
        pageSize: 12,
        ...(searchTerm && { search: searchTerm }),
        ...(activeTab === 'community' && { sortBy }),
        ...(activeTab === 'my-prompts' && { searchBy }),
        ...(activeTab === 'my-prompts' && mySortBy && { sortBy: mySortBy }),
        ...(activeTab === 'my-prompts' && (mySortBy === 'displayName' || mySortBy === 'topic') && { sortDir: mySortDir })
      });

      const response = await fetch(`${endpoint}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch prompts');
      }

      const data = await response.json();
      setPrompts(data.prompts);
      setTotalPages(data.totalPages);
    } catch (err) {
      setError('Failed to load prompts');
      console.error('Error fetching prompts:', err);
    } finally {
      setLoading(false);
    }
  };

  const refreshPrompt = async (id) => {
    try {
      const token = authService.getToken();
      const r = await fetch(`/api/savedprompts/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (!r.ok) return;
      const dto = await r.json();
      setPrompts(prev => prev.map(p => p.id === id ? {
        ...p,
        title: dto.title,
        displayName: dto.displayName,
        content: dto.content,
        description: dto.description,
        isPublic: dto.isPublic,
        isFlagged: dto.isFlagged,
        // ensure topic/subtopic display stays in sync immediately after edits
        topicName: dto.topicName,
        subtopicName: dto.subtopicName,
        rawTopic: dto.rawTopic,
        rawSubtopic: dto.rawSubtopic,
        categoryName: dto.categoryName
      } : p));
    } catch {}
  };

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const hideToast = () => {
    setToast(prev => ({ ...prev, show: false }));
  };

  const handleUsePrompt = async (promptId) => {
    try {
      console.log('SavedPrompts: handleUsePrompt called with promptId:', promptId);
      const token = authService.getToken();
      console.log('SavedPrompts: Token available:', !!token);
      
      const response = await fetch(`/api/savedprompts/${promptId}/use`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('SavedPrompts: API response status:', response.status);
      console.log('SavedPrompts: API response ok:', response.ok);

      if (response.ok) {
        // Navigate to prompt generator with the saved prompt data
        const savedPromptData = prompts.find(p => p.id === promptId);
        console.log('SavedPrompts: Navigating with saved prompt data:', savedPromptData);
        console.log('SavedPrompts: savedPromptData.content:', savedPromptData?.content);
        
        // Test: Also try setting a URL parameter as backup
        navigate('/prompt-generator', { 
          state: { 
            savedPrompt: savedPromptData 
          } 
        });
      }
    } catch (err) {
      console.error('Error using prompt:', err);
    }
  };

  const handleDeletePrompt = (promptId) => {
    // Show confirmation modal instead of immediate deletion
    const prompt = prompts.find(p => p.id === promptId);
    setPromptToDelete(prompt);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!promptToDelete) return;

    // Close modal and show progress
    setDeleteConfirmOpen(false);
    showToast('Deleting prompt...', 'info');

    try {
      const token = authService.getToken();
      const response = await fetch(`/api/savedprompts/${promptToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        setPrompts(prompts.filter(p => p.id !== promptToDelete.id));
        showToast('Prompt deleted successfully!', 'success');
        // After deletion, re-fetch to keep pagination and counts accurate
        await fetchPrompts();
      } else {
        const errorText = await response.text();
        console.error('Failed to delete prompt:', errorText);
        showToast(`Failed to delete prompt: ${errorText}`, 'error');
      }
    } catch (err) {
      console.error('Error deleting prompt:', err);
      showToast(`Failed to delete prompt: ${err.message}`, 'error');
    } finally {
      setPromptToDelete(null);
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setPromptToDelete(null);
  };

  const handleEditPrompt = (prompt) => {
    setEditingPrompt(prompt);
    setEditModalOpen(true);
  };

  const handleSaveEditedPrompt = async (updatedPrompt) => {
    try {
      const token = authService.getToken();
      // Extract topic/subtopic from edited content
      const { rawTopic, rawSubtopic } = (() => {
        const text = updatedPrompt.content || '';
        const focusPattern = /Focus on\s+([^,.\n\r]+)(?:,\s*specifically addressing\s+([^\.\n\r]+))?\./i;
        const m = text.match(focusPattern);
        if (m) {
          return { rawTopic: (m[1] || '').trim(), rawSubtopic: (m[2] || '').trim() };
        }
        return { rawTopic: null, rawSubtopic: null };
      })();

      const response = await fetch(`/api/savedprompts/${updatedPrompt.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: updatedPrompt.title,
          displayName: updatedPrompt.displayName,
          content: updatedPrompt.content,
          description: updatedPrompt.description,
          isPublic: updatedPrompt.isPublic,
          categoryId: updatedPrompt.categoryId,
          topicId: updatedPrompt.topicId,
          subtopicId: updatedPrompt.subtopicId,
          rawTopic: rawTopic || updatedPrompt.rawTopic,
          rawSubtopic: rawSubtopic || updatedPrompt.rawSubtopic,
          styleTemplateId: updatedPrompt.styleTemplateId,
          outcomeTemplateId: updatedPrompt.outcomeTemplateId,
          aiModelId: updatedPrompt.aiModelId,
          tags: updatedPrompt.tags
        })
      });

      if (response.ok) {
        const resBody = await response.json();
        // Update the prompt in the local state
        setPrompts(prev => prev.map(p => 
          p.id === updatedPrompt.id ? { 
            ...p, 
            ...updatedPrompt, 
            displayName: updatedPrompt.displayName,
            title: resBody.title ?? (rawTopic || updatedPrompt.title),
            isPublic: resBody.isPublic, 
            isFlagged: resBody.isFlagged, 
            rawTopic: resBody.rawTopic ?? (rawTopic || updatedPrompt.rawTopic), 
            rawSubtopic: resBody.rawSubtopic ?? (rawSubtopic || updatedPrompt.rawSubtopic) 
          } : p
        ));
        showToast(resBody.message || 'Prompt updated successfully!', resBody.isFlagged ? 'warning' : 'success');
        await refreshPrompt(updatedPrompt.id);
        // Ensure full list is consistent (e.g., pagination counts, any server-side moderation effects)
        await fetchPrompts();
      } else {
        const errorText = await response.text();
        console.error('Failed to update prompt:', response.status, errorText);
        showToast('Failed to update prompt. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Error updating prompt:', err);
      showToast('Failed to update prompt. Please try again.', 'error');
    }
  };

  const handleTogglePrivacy = async (prompt) => {
    const newIsPublic = !prompt.isPublic;
    
    // If making public, show confirmation modal
    if (newIsPublic) {
      setPromptToMakePublic(prompt);
      setPublicConfirmOpen(true);
      return;
    }
    
    // If making private, proceed directly
    await updatePromptPrivacy(prompt, newIsPublic);
  };

  const confirmMakePublic = async () => {
    if (!promptToMakePublic) return;
    
    setPublicConfirmOpen(false);
    // Frontend preflight profanity check using bad-words
    try {
      const Filter = (await import('bad-words')).default;
      const filter = new Filter();
      const title = promptToMakePublic.title || '';
      const content = promptToMakePublic.content || '';
      const profane = filter.isProfane(title) || filter.isProfane(content);
      if (profane) {
        // Request admin review instead of making public
        const token = authService.getToken();
        await fetch(`/api/savedprompts/${promptToMakePublic.id}/request-review`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: 'Auto-detected potential profanity via client precheck' })
        });
        showToast('Your prompt appears to contain disallowed language. It has been sent for review and remains private.', 'warning');
        setPromptToMakePublic(null);
        return;
      }
    } catch {}

    await updatePromptPrivacy(promptToMakePublic, true);
    setPromptToMakePublic(null);
  };

  const cancelMakePublic = () => {
    setPublicConfirmOpen(false);
    setPromptToMakePublic(null);
  };

  const updatePromptPrivacy = async (prompt, newIsPublic) => {
    showToast(
      newIsPublic ? 'Making prompt public...' : 'Making prompt private...', 
      'info'
    );

    try {
      const token = authService.getToken();
      const response = await fetch(`/api/savedprompts/${prompt.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: prompt.title,
          content: prompt.content,
          description: prompt.description,
          isPublic: newIsPublic,
          categoryId: prompt.categoryId,
          topicId: prompt.topicId,
          subtopicId: prompt.subtopicId,
          rawTopic: prompt.rawTopic,
          rawSubtopic: prompt.rawSubtopic,
          styleTemplateId: prompt.styleTemplateId,
          outcomeTemplateId: prompt.outcomeTemplateId,
          aiModelId: prompt.aiModelId,
          tags: prompt.tags
        })
      });

      if (response.ok) {
        const resBody = await response.json();
        // Update the prompt in the local state
        setPrompts(prev => prev.map(p => 
          p.id === prompt.id ? { ...p, isPublic: resBody.isPublic, isFlagged: resBody.isFlagged, rawTopic: resBody.rawTopic ?? p.rawTopic, rawSubtopic: resBody.rawSubtopic ?? p.rawSubtopic, title: resBody.title ?? p.title } : p
        ));
        showToast(
          resBody.message || (resBody.isPublic ? 'Prompt made public successfully!' : 'Prompt made private successfully!'),
          resBody.isFlagged ? 'warning' : 'success'
        );
        await refreshPrompt(prompt.id);
        // Reload list to reflect any server-side filtering (e.g., flagged prompts not appearing in community)
        await fetchPrompts();
      } else {
        const errorText = await response.text();
        console.error('Failed to update prompt privacy:', response.status, errorText);
        showToast('Failed to update prompt privacy. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Error updating prompt privacy:', err);
      showToast('Failed to update prompt privacy. Please try again.', 'error');
    }
  };

  const handleLikePrompt = async (promptId) => {
    try {
      const token = authService.getToken();
      const response = await fetch(`/api/savedprompts/${promptId}/like`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update the prompt's like count and like status in the local state
        setPrompts(prompts.map(p => 
          p.id === promptId ? { 
            ...p, 
            likeCount: result.newLikeCount,
            isLikedByCurrentUser: result.isLiked
          } : p
        ));

        showToast(result.message, 'success');
      } else {
        showToast('Failed to like prompt. Please try again.', 'error');
      }
    } catch (err) {
      console.error('Error liking prompt:', err);
      showToast('Failed to like prompt. Please try again.', 'error');
    }
  };

  const handleSaveCommunityPrompt = async (prompt) => {
    try {
      const token = authService.getToken();
      if (!token) {
        showToast('Please log in to save prompts', 'error');
        return;
      }

      // Check if this is the user's own prompt
      if (currentUser && prompt.userId === currentUser.id) {
        showToast('You cannot save your own prompt to your collection', 'warning');
        return;
      }

      const promptData = {
        title: prompt.title || 'Community Prompt',
        content: prompt.content,
        description: prompt.description || `Saved from community: ${prompt.userName?.split('@')[0] || 'Anonymous'}`,
        isPublic: false, // Always save as private
        categoryId: prompt.categoryId,
        topicId: prompt.topicId,
        subtopicId: prompt.subtopicId,
        rawTopic: prompt.rawTopic,
        rawSubtopic: prompt.rawSubtopic,
        styleTemplateId: prompt.styleTemplateId,
        outcomeTemplateId: prompt.outcomeTemplateId,
        aiModelId: prompt.aiModelId,
        tags: `community ${prompt.categoryName || ''} ${prompt.rawTopic || ''}`.trim()
      };

      const response = await fetch('/api/savedprompts', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(promptData)
      });

      if (response.ok) {
        showToast('Prompt saved to your collection!', 'success');
      } else {
        let serverMsg = null;
        try { serverMsg = await response.json(); } catch { try { serverMsg = await response.text(); } catch {}
        }
        if (serverMsg && typeof serverMsg === 'object' && serverMsg.code === 'free_saved_limit') {
          showToast(serverMsg.message || 'Free plan limit reached: up to 12 saved prompts.', 'warning');
        } else {
          console.error('Failed to save community prompt:', serverMsg || response.status);
          showToast('Failed to save prompt. Please try again.', 'error');
        }
      }
    } catch (err) {
      console.error('Error saving community prompt:', err);
      showToast('Failed to save prompt. Please try again.', 'error');
    }
  };

  const handleForkPrompt = async (promptId) => {
    try {
      const token = authService.getToken();
      if (!token) { showToast('Please log in', 'error'); return; }
      const resp = await fetch(`/api/savedprompts/${promptId}/fork`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (!resp.ok) throw new Error(await resp.text());
      showToast('Saved to My Prompts', 'success');
    } catch (e) {
      console.error(e);
      showToast('Failed to save', 'error');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return '1 day ago';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  // Utility function to truncate text to 25 characters
  const truncateText = (text, maxLength = 25) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  if (loading) {
    return (
      <div className="saved-prompts-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="saved-prompts-container">
      <div className="saved-prompts-header">
        <h1>Saved Prompts</h1>
        <div className="tab-navigation">
          <button 
            className={`tab-button ${activeTab === 'my-prompts' ? 'active' : ''}`}
            onClick={() => setActiveTab('my-prompts')}
          >
            My Prompts
          </button>
          <button 
            className={`tab-button ${activeTab === 'community' ? 'active' : ''}`}
            onClick={() => setActiveTab('community')}
          >
            Community
          </button>
          <button 
            className={`tab-button ${activeTab === 'team' ? 'active' : ''}`}
            onClick={() => setActiveTab('team')}
          >
            Team
          </button>
          <button 
            className={`tab-button ${activeTab === 'org' ? 'active' : ''}`}
            onClick={() => setActiveTab('org')}
          >
            Org
          </button>
        </div>
      </div>

      <div className="search-and-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search prompts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        {activeTab === 'community' && (
          <div className="sort-controls">
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="recent">Most Recent</option>
              <option value="popular">Most Popular</option>
              <option value="oldest">Oldest</option>
            </select>
          </div>
        )}

        {activeTab === 'my-prompts' && (
          <div className="sort-controls">
            <select 
              value={searchBy}
              onChange={(e) => setSearchBy(e.target.value)}
              className="sort-select"
              title="Search by"
            >
              <option value="both">Search by: Both</option>
              <option value="displayName">Search by: Display Name</option>
              <option value="topic">Search by: Topic</option>
            </select>
            <select 
              value={mySortBy}
              onChange={(e) => setMySortBy(e.target.value)}
              className="sort-select"
              title="Sort by"
            >
              <option value="recent">Sort by: Most Recent</option>
              <option value="oldest">Sort by: Oldest</option>
              <option value="displayName">Sort by: Display Name</option>
              <option value="topic">Sort by: Topic</option>
            </select>
            {(mySortBy === 'displayName' || mySortBy === 'topic') && (
              <select 
                value={mySortDir}
                onChange={(e) => setMySortDir(e.target.value)}
                className="sort-select"
                title="Sort direction"
              >
                <option value="asc">A–Z</option>
                <option value="desc">Z–A</option>
              </select>
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="error-message">{error}</div>
      )}

      <div className="prompts-grid">
        {prompts.map((prompt) => (
          <div key={prompt.id} className="prompt-card">
            <div className="prompt-header">
              <div className="prompt-title">
                <div className="topic-label">Display Name</div>
                <div className="topic-value">
                  {(() => {
                    const fallback = (() => {
                      if (prompt.topicName) {
                        return prompt.subtopicName 
                          ? `${prompt.topicName} / ${prompt.subtopicName}`
                          : prompt.topicName;
                      } else if (prompt.rawTopic) {
                        return prompt.rawSubtopic 
                          ? `${prompt.rawTopic} / ${prompt.rawSubtopic}`
                          : prompt.rawTopic;
                      } else {
                        return prompt.title || 'Untitled';
                      }
                    })();
                    return (prompt.displayName && prompt.displayName.trim()) ? prompt.displayName : fallback;
                  })()}
                </div>
              </div>
            </div>
            
            {/* Removed mid-card description to avoid showing generated-for text; category is shown below */}

            <div className="prompt-meta">
              <div className="meta-item">
                <span className="meta-label">Category:</span>
                <span className="meta-value">{truncateText(prompt.categoryName || 'N/A')}</span>
              </div>

              <div className="meta-item">
                <span className="meta-label">Created:</span>
                <span className="meta-value">{formatDate(prompt.createdDate)}</span>
              </div>
              {activeTab === 'community' && (
                <div className="meta-item">
                  <span className="meta-label">By:</span>
                  <span className="meta-value">{truncateText(prompt.userName?.split('@')[0] || 'Anonymous')}</span>
                </div>
              )}
            </div>

            <div className="prompt-stats">
              <div className="stats-row">
                {activeTab === 'community' ? (
                  <button 
                    onClick={() => handleLikePrompt(prompt.id)}
                    className={`like-button ${prompt.isLikedByCurrentUser ? 'liked' : ''}`}
                    title={prompt.isLikedByCurrentUser ? 'Unlike this prompt' : 'Like this prompt'}
                    disabled={prompt.isLikedByCurrentUser || (currentUser && prompt.userId === currentUser.id)}
                  >
                    {prompt.isLikedByCurrentUser ? '❤️' : '🤍'} {prompt.likeCount}
                  </button>
                ) : (
                  <span className="stat-item">❤️ {prompt.likeCount}</span>
                )}
                <span className="stat-item">📊 {prompt.useCount} uses</span>
                <div className="privacy-area">
                  {prompt.isFlagged && (
                    <span className="flagged-indicator" title="Pending moderation">🚩</span>
                  )}
                  <button 
                    onClick={() => activeTab === 'my-prompts' ? handleTogglePrivacy(prompt) : null}
                    className={`privacy-toggle ${prompt.isPublic ? 'public' : 'private'} ${activeTab === 'community' ? 'disabled' : ''}`}
                    title={
                      activeTab === 'community' 
                        ? 'Community prompts cannot be modified' 
                        : (prompt.isPublic ? 'Click to make private' : 'Click to make public')
                    }
                    disabled={activeTab === 'community'}
                  >
                    {prompt.isPublic ? (
                      <span className="privacy-badge public">🌐 Public</span>
                    ) : (
                      <span className="privacy-badge private">🔒 Private</span>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="prompt-actions">
              <button onClick={() => handleUsePrompt(prompt.id)} className="action-button primary">Use</button>
              
              {activeTab === 'my-prompts' && (
                <>
                  <button onClick={() => handleEditPrompt(prompt)} className="action-button secondary">Edit</button>
                  <button onClick={() => handleDeletePrompt(prompt.id)} className="action-button danger">Delete</button>
                  <button onClick={() => { setShareTargetId(prompt.id); setShareOpen(true); }} className="action-button">Share</button>
                </>
              )}
              
              {(activeTab === 'community' || activeTab === 'team' || activeTab === 'org') && (
                <button 
                  onClick={() => handleForkPrompt(prompt.id)}
                  className="action-button secondary"
                >
                  Save to My Prompts
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {prompts.length === 0 && !loading && (
        <div className="empty-state">
          <h3>No prompts found</h3>
          <p>
            {activeTab === 'my-prompts' 
              ? 'You haven\'t saved any prompts yet. Create your first prompt!'
              : 'No community prompts match your search criteria.'
            }
          </p>
          {activeTab === 'my-prompts' && (
            <button 
              onClick={() => navigate('/prompt-generator')}
              className="action-button primary"
            >
              Create Your First Prompt
            </button>
          )}
        </div>
      )}

      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            Previous
          </button>
          
          <span className="page-info">
            Page {currentPage} of {totalPages}
          </span>
          
          <button 
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            Next
          </button>
        </div>
      )}

      {/* Edit Modal */}
      <EditPromptModal
        prompt={editingPrompt}
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingPrompt(null);
        }}
        onSave={handleSaveEditedPrompt}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div className="help-modal-overlay" onClick={cancelDelete}>
          <div className="help-modal" style={{ maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div className="help-modal-header">
              <h2>Confirm Delete</h2>
              <button onClick={cancelDelete} className="help-modal-close" title="Close">&times;</button>
            </div>
            
            <div className="help-modal-content" style={{ padding: '2rem' }}>
              <p style={{ marginBottom: '1rem', fontSize: '1rem', lineHeight: '1.5' }}>
                Are you sure you want to delete the prompt <strong>"{promptToDelete?.title}"</strong>?
              </p>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                This action cannot be undone.
              </p>
            </div>
            
            <div className="help-modal-footer" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', padding: '1.5rem 2rem', backgroundColor: '#f8f9fa', borderTop: '1px solid #e9ecef' }}>
              <button 
                onClick={cancelDelete} 
                className="help-modal-btn help-modal-btn-cancel"
                style={{ padding: '0.75rem 1.5rem', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '1rem', minWidth: '100px', backgroundColor: '#6c757d', color: 'white' }}
              >
                Cancel
              </button>
              <button 
                onClick={confirmDelete} 
                className="help-modal-btn help-modal-btn-danger"
                style={{ padding: '0.75rem 1.5rem', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '1rem', minWidth: '100px', backgroundColor: '#dc3545', color: 'white' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Public Confirmation Modal */}
      {publicConfirmOpen && (
        <div className="help-modal-overlay" onClick={cancelMakePublic}>
          <div className="help-modal" style={{ maxWidth: '450px' }} onClick={(e) => e.stopPropagation()}>
            <div className="help-modal-header">
              <h2>Make Prompt Public</h2>
              <button onClick={cancelMakePublic} className="help-modal-close" title="Close">&times;</button>
            </div>
            
            <div className="help-modal-content" style={{ padding: '2rem' }}>
              <p style={{ marginBottom: '1rem', fontSize: '1rem', lineHeight: '1.5' }}>
                Are you sure you want to make the prompt <strong>"{promptToMakePublic?.title}"</strong> public?
              </p>
              <p style={{ margin: 0, fontSize: '0.9rem', color: '#666' }}>
                This will make your prompt visible to all users in the community section.
              </p>
            </div>
            
            <div className="help-modal-footer" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', padding: '1.5rem 2rem', backgroundColor: '#f8f9fa', borderTop: '1px solid #e9ecef' }}>
              <button 
                onClick={cancelMakePublic} 
                className="help-modal-btn help-modal-btn-cancel"
                style={{ padding: '0.75rem 1.5rem', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '1rem', minWidth: '100px', backgroundColor: '#6c757d', color: 'white' }}
              >
                Cancel
              </button>
              <button 
                onClick={confirmMakePublic} 
                className="help-modal-btn help-modal-btn-primary"
                style={{ padding: '0.75rem 1.5rem', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', fontSize: '1rem', minWidth: '100px', backgroundColor: '#007bff', color: 'white' }}
              >
                Make Public
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {shareOpen && (
        <div className="help-modal-overlay" onClick={() => setShareOpen(false)}>
          <div className="help-modal" style={{ maxWidth: '520px' }} onClick={(e) => e.stopPropagation()}>
            <div className="help-modal-header">
              <h2>Share Prompt</h2>
              <button onClick={() => setShareOpen(false)} className="help-modal-close" title="Close">&times;</button>
            </div>
            <div className="help-modal-content" style={{ padding: '1.5rem' }}>
              {isTeamOrOrgUser && (
                <div style={{ marginBottom: '12px', padding: '12px', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', fontSize: '14px', color: '#92400e' }}>
                  Team/Org users can only share prompts with their own teams/organizations, not with individual users.
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                <label style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <span>Principal Type</span>
                  <select value={sharePrincipalType} onChange={(e) => {
                    setSharePrincipalType(e.target.value);
                    setSharePrincipalId(''); // Reset when changing type
                  }}>
                    <option value="team">Team</option>
                    <option value="org">Org</option>
                    {!isTeamOrOrgUser && <option value="user">User</option>}
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                  <span>Role</span>
                  <select value={shareRole} onChange={(e) => setShareRole(e.target.value)}>
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                  </select>
                </label>
              </div>

              {/* Team/Org dropdowns for Team/Org users */}
              {isTeamOrOrgUser && sharePrincipalType === 'team' && userTeams.length > 0 && (
                <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '12px' }}>
                  <span>Select Team</span>
                  <select
                    value={sharePrincipalId}
                    onChange={(e) => setSharePrincipalId(e.target.value)}
                  >
                    <option value="">Choose a team…</option>
                    {userTeams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name || `Team ${team.id}`}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {isTeamOrOrgUser && sharePrincipalType === 'org' && userOrgs.length > 0 && (
                <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '12px' }}>
                  <span>Select Organization</span>
                  <select
                    value={sharePrincipalId}
                    onChange={(e) => setSharePrincipalId(e.target.value)}
                  >
                    <option value="">Choose an organization…</option>
                    {userOrgs.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name || `Organization ${org.id}`}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {/* If user principal and we have groups/members, show dropdown selector */}
              {!isTeamOrOrgUser && sharePrincipalType === 'user' && recipientGroups && recipientGroups.some(g => Array.isArray(g.members) && g.members.length > 0) && (
                <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '12px' }}>
                  <span>Select Recipient</span>
                  <select
                    value={sharePrincipalId}
                    onChange={(e) => setSharePrincipalId(e.target.value)}
                  >
                    <option value="">Choose a recipient…</option>
                    {recipientGroups.map((g, gi) => (
                      <optgroup key={`g-${gi}`} label={g.groupName || g.name || `Group ${gi + 1}`}>
                        {(g.members || []).map((m, mi) => (
                          <option key={`m-${gi}-${mi}`} value={m.userId || m.id || ''}>
                            {`${m.name || m.displayName || m.email || (m.userId || '').slice(0,8)}${m.email ? ` <${m.email}>` : ''}`}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </label>
              )}

              {/* Show input field only if not using dropdowns */}
              {(!isTeamOrOrgUser || (sharePrincipalType === 'team' && userTeams.length === 0) || (sharePrincipalType === 'org' && userOrgs.length === 0)) && (
                <label style={{ display: 'flex', flexDirection: 'column', marginBottom: '12px' }}>
                  <span>{sharePrincipalType === 'user' ? 'User Id' : (sharePrincipalType === 'team' ? 'Team Id' : 'Org Id')}</span>
                  <input
                    value={sharePrincipalId}
                    onChange={(e) => setSharePrincipalId(e.target.value)}
                    placeholder={sharePrincipalType === 'user' ? 'user id' : 'numeric id'}
                  />
                </label>
              )}
              <label style={{ display: 'flex', flexDirection: 'column' }}>
                <span>Expires At (optional)</span>
                <input type="datetime-local" value={shareExpiresAt} onChange={(e) => setShareExpiresAt(e.target.value)} />
              </label>
            </div>
            <div className="help-modal-footer" style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', padding: '1rem' }}>
              <button onClick={() => setShareOpen(false)} className="help-modal-btn help-modal-btn-cancel">Cancel</button>
              <button onClick={async () => {
                try {
                  const token = authService.getToken();
                  const body = {
                    principalType: sharePrincipalType,
                    principalId: sharePrincipalId,
                    role: shareRole,
                    expiresAt: shareExpiresAt ? new Date(shareExpiresAt).toISOString() : null
                  };
                  const resp = await fetch(`/api/savedprompts/${shareTargetId}/share`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                    body: JSON.stringify(body)
                  });
                  if (!resp.ok) throw new Error(await resp.text());
                  showToast('Share updated', 'success');
                  setShareOpen(false);
                  setSharePrincipalId('');
                } catch (e) {
                  console.error(e);
                  showToast('Failed to share', 'error');
                }
              }} className="help-modal-btn help-modal-btn-primary">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={hideToast}
      />
    </div>
  );
};

export default SavedPrompts; 